import { PropsWithChildren, useEffect, useMemo } from "react";
import { myplantThemes } from "@myplant-io/app-components";
import {
  createTheme,
  ThemeProvider as MUIThemeProvider,
  useMediaQuery,
} from "@mui/material";
import { useAuth } from "@myplant-io/hooks";
import { useAppState } from "./App";
import { API_BASE_URL } from "../constants";

export function ThemeProvider({ children }: PropsWithChildren<unknown>) {
  const {
    state: { appearance },
    actions: { setAppearance },
  } = useAppState();
  const { data } = useAuth(API_BASE_URL);
  const authAppearance = data?.user.profile.appearance;

  // When the auth value arrives, persist it to app state so the next load
  // can apply the correct theme before the auth response arrives.
  useEffect(() => {
    if (authAppearance && authAppearance !== appearance) {
      setAppearance(authAppearance);
    }
  }, [authAppearance, setAppearance, appearance]);

  // Use the server value when available, fall back to the cached value.
  const appearanceWithFallback = authAppearance ?? appearance;

  // Reactive: re-renders when the OS color scheme changes.
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");

  // Resolve "system" using the reactive OS/browser color-scheme preference.
  const resolvedAppearance =
    appearanceWithFallback === "system"
      ? prefersDark
        ? "dark"
        : "light"
      : appearanceWithFallback;

  const theme = useMemo(
    () =>
      createTheme({
        ...(resolvedAppearance === "dark"
          ? myplantThemes.dark
          : myplantThemes.light),
        typography: {
          fontFamily: [
            "innio",
            "Segoe UI",
            "Segoe UI Web (West European)",
            "Segoe UI",
          ].join(","),
        },
        breakpoints: {
          values: {
            xs: 0,
            sm: 640,
            md: 768,
            lg: 1024,
            "2lg": 1120, // additional breakpoint
            xl: 1280,
          },
        },
      }),
    [resolvedAppearance]
  );

  return <MUIThemeProvider theme={theme}>{children}</MUIThemeProvider>;
}
